import { Combobox, DoubleListSelector } from '@mairistem/finances-utils';
import type { DoubleListLineType } from '@mairistem/finances-utils/lib/ts/components/DoubleListSelector/DoubleListLine';
import { maxBy } from 'lodash';
import React from 'react';
import { toast } from 'react-toastify';
import { Button, Form } from 'semantic-ui-react';
import TypeBudget from '../../../constants/simulation/typeBudget';
import type MontantReference from '../../../interfaces/simulation/montantReference';
import {
	createMontantReference,
	getMontantsReferences,
	updateMontantReference,
} from '../../../services/simulation/montantReference';
import './MontantReferenceList.css';
import {
	getMontantReferenceLibelle,
	MONTANT_REFERENCE,
} from '../../../constants/simulation/montantReference';
import type LigneMontantReference from '../../../interfaces/simulation/ligneMontantReference';
import { getDefaultMontantReference } from '../../../utils/montantSimulation';
import { useSimulationCourante } from '../../common/Context/simulationStore';

const ETAPE_BUDGETAIRE_OPTIONS = [
	{
		key: 'budget_primitif',
		text: 'Budget Primitif',
		value: TypeBudget.BUDGET_PRIMITIF,
	},
	{
		key: 'budget_supplementaire',
		text: 'Budget Supplémentaire',
		value: TypeBudget.BUDGET_SUPPLEMENTAIRE,
	},
	{
		key: 'compte_administratif',
		text: 'Compte Administratif',
		value: TypeBudget.COMPTE_ADMINISTRATIF,
	},
	{
		key: 'decision_modificative',
		text: 'Décision Modificative',
		value: TypeBudget.DECISION_MODIFICATIVE,
	},
	{
		key: 'virement_interne',
		text: 'Virement Interne',
		value: TypeBudget.VIREMENT_INTERNE,
	},
];

const MONTANT_REFERENCE_OPTIONS = [
	{
		code: MONTANT_REFERENCE.BUDGET,
		libelle: getMontantReferenceLibelle(MONTANT_REFERENCE.BUDGET),
	},
	{
		code: MONTANT_REFERENCE.BUDGET_N1,
		libelle: getMontantReferenceLibelle(MONTANT_REFERENCE.BUDGET_N1),
	},
	{
		code: MONTANT_REFERENCE.BUDGET_N2,
		libelle: getMontantReferenceLibelle(MONTANT_REFERENCE.BUDGET_N2),
	},
	{
		code: MONTANT_REFERENCE.REALISE,
		libelle: getMontantReferenceLibelle(MONTANT_REFERENCE.REALISE),
	},
	{
		code: MONTANT_REFERENCE.REALISE_N1,
		libelle: getMontantReferenceLibelle(MONTANT_REFERENCE.REALISE_N1),
	},
	{
		code: MONTANT_REFERENCE.REALISE_N2,
		libelle: getMontantReferenceLibelle(MONTANT_REFERENCE.REALISE_N2),
	},
	{
		code: MONTANT_REFERENCE.BP_N1,
		libelle: getMontantReferenceLibelle(MONTANT_REFERENCE.BP_N1),
	},
	{
		code: MONTANT_REFERENCE.BP_N2,
		libelle: getMontantReferenceLibelle(MONTANT_REFERENCE.BP_N2),
	},

	{
		code: MONTANT_REFERENCE.ENGAGE,
		libelle: getMontantReferenceLibelle(MONTANT_REFERENCE.ENGAGE),
	},
	{
		code: MONTANT_REFERENCE.LIQUIDE,
		libelle: getMontantReferenceLibelle(MONTANT_REFERENCE.LIQUIDE),
	},
];

const MontantReferenceList = () => {
	// Positionnement par defaut sur le type de budget de la simulation courante
	// (connue si l'on vient de l'ecran d'elaboration), sinon sur le BP
	const typeBudgetSimulation = useSimulationCourante(
		(simulation) => simulation?.budget.type,
	);
	const [etapeBudgetaire, setEtapeBudgetaire] = React.useState(
		typeBudgetSimulation ?? TypeBudget.BUDGET_PRIMITIF,
	);

	const [loadingMontantsReferences, setLoadingMontantsReferences] =
		React.useState(true);

	const [montantsReferences, setMontantsReferences] = React.useState<
		MontantReference[]
	>([]);

	const fetchMontantsReferences = async () => {
		setLoadingMontantsReferences(true);
		try {
			const response = await getMontantsReferences(
				new URLSearchParams({
					all: 'true',
					withLignes: 'true',
				}),
			);
			setMontantsReferences(response.data);
			createDefaultMontantReference(etapeBudgetaire, response.data);
		} catch (_error) {
			toast.error('Impossible de charger les montants de références');
		} finally {
			setLoadingMontantsReferences(false);
		}
	};

	// biome-ignore lint/correctness/useExhaustiveDependencies: pas besoin
	React.useEffect(() => {
		fetchMontantsReferences();
	}, []);

	const createDefaultMontantReference = async (
		etapeBudgetaire: string,
		montants: MontantReference[],
	) => {
		const montantReferenceExists = montants.some(
			(montant) => montant.etapeBudgetaire === etapeBudgetaire,
		);

		if (!montantReferenceExists) {
			try {
				setLoadingMontantsReferences(true);
				const newMontantsReferences = await createMontantReference({
					etapeBudgetaire,
					lignes: getDefaultMontantReference(
						etapeBudgetaire as TypeBudget,
					) as LigneMontantReference[],
				} as MontantReference);

				setMontantsReferences((prev) => [...prev, newMontantsReferences]);
			} catch (_e) {
				toast.error(
					'Une erreur est survenue lors de la création du montant de référence',
				);
			} finally {
				setLoadingMontantsReferences(false);
			}
		}
	};

	const handleChangeEtapeBudgetaire = (_e, { value }) => {
		setEtapeBudgetaire(value);
		createDefaultMontantReference(value, montantsReferences);
	};

	const montantReference = montantsReferences.find(
		(montant) => montant.etapeBudgetaire === etapeBudgetaire,
	);

	const rightValues = React.useMemo(
		() =>
			montantReference?.lignes?.map(({ typeMontant }) => ({
				identifiant: typeMontant,
				libelle:
					MONTANT_REFERENCE_OPTIONS.find((f) => f.code === typeMontant)
						?.libelle ?? '',
			})),
		[montantReference?.lignes],
	);

	const leftValues = React.useMemo(
		() =>
			MONTANT_REFERENCE_OPTIONS?.reduce((previous, next) => {
				const isSelected = montantReference?.lignes?.some(
					(mt) => mt?.typeMontant === next.code,
				);
				if (!isSelected) {
					previous.push({
						identifiant: next.code,
						libelle: next.libelle,
					});
				}

				return previous;
			}, []),
		[montantReference?.lignes],
	);

	const handleAddItems = async (montants: DoubleListLineType[]) => {
		setLoadingMontantsReferences(true);
		const i = maxBy(montantReference?.lignes, (l) => l?.ordre)?.ordre ?? 0;
		const updatedMontantReference = {
			...montantReference,
			lignes: [
				...(montantReference?.lignes ?? []),
				...montants.map((montant, index) => ({
					typeMontant: montant.identifiant,
					ordre: i + index + 1,
				})),
			],
		} as MontantReference;

		try {
			const newMontant = await updateMontantReference(updatedMontantReference);

			const updatedMontantsReferences = montantsReferences.map((montant) => {
				if (montant.etapeBudgetaire === etapeBudgetaire) {
					return newMontant;
				}
				return montant;
			});
			setMontantsReferences(updatedMontantsReferences);
		} catch (_e) {
			toast.error("Impossible d'ajouter le montant de référence");
		} finally {
			setLoadingMontantsReferences(false);
		}
	};

	const handleDragDrop = async (result) => {
		let montants = [...montantReference.lignes];

		const destinationOrdre = montants[result.destination.index].ordre;

		if (result.destination.index > result.source.index) {
			for (
				let i = result.source.index + 1;
				i <= result.destination.index;
				i++
			) {
				montants[i].ordre -= 1;
			}
		} else if (result.destination.index < result.source.index) {
			for (let i = result.destination.index; i < result.source.index; i++) {
				montants[i].ordre += 1;
			}
		}

		montants[result.source.index].ordre = destinationOrdre;
		montants = montants.sort((a, b) => a.ordre - b.ordre);
		try {
			const updatedMontantReference = {
				...montantReference,
				lignes: montants,
			} as MontantReference;

			setMontantsReferences((prev) =>
				prev.map((montant) =>
					montant.etapeBudgetaire === etapeBudgetaire
						? {
								...updatedMontantReference,
								lignes: updatedMontantReference.lignes.sort(
									(a, b) => a.ordre - b.ordre,
								),
							}
						: montant,
				),
			);

			setLoadingMontantsReferences(true);
			await updateMontantReference(updatedMontantReference);
		} catch (_e) {
			toast.error("Impossible d'ajouter le montant de référence");
		} finally {
			setLoadingMontantsReferences(false);
		}
	};

	const handleDeleteMontantReference = async (identifiant: string) => {
		try {
			const identifiantMontantReference = montantReference?.lignes?.find(
				(ligne) => ligne.typeMontant === identifiant,
			)?.identifiant;

			const updatedMontantReference = {
				...montantReference,
				lignes: montantReference?.lignes?.filter(
					(ligne) => ligne.typeMontant !== identifiant,
				),
			};

			await updateMontantReference({
				...updatedMontantReference,
				deletedLignes: [identifiantMontantReference],
			});

			const updatedMontantsReferences = montantsReferences.map((montant) => {
				if (montant.etapeBudgetaire === etapeBudgetaire) {
					return updatedMontantReference;
				}
				return montant;
			});
			setMontantsReferences(updatedMontantsReferences);
		} catch (_e) {
			toast.error('Impossible de supprimer le montant de référence');
		}
	};

	const handleReset = async () => {
		try {
			setLoadingMontantsReferences(true);
			const updatedMontantReference = {
				...montantReference,
				lignes: getDefaultMontantReference(
					etapeBudgetaire,
				) as LigneMontantReference[],
				deletedLignes: montantReference?.lignes?.map(
					(ligne) => ligne.identifiant,
				),
			} as MontantReference;

			const newMontant = await updateMontantReference(updatedMontantReference);

			const updatedMontantsReferences = montantsReferences.map((montant) => {
				if (montant.etapeBudgetaire === etapeBudgetaire) {
					return newMontant;
				}
				return montant;
			});
			setMontantsReferences(updatedMontantsReferences);
		} catch (_e) {
			toast.error(
				'Une erreur est survenue lors de la réinitialisation du montant de référence',
			);
		} finally {
			setLoadingMontantsReferences(false);
		}
	};

	return (
		<>
			<Form className="montant-reference-form">
				<Form.Group>
					<Form.Field
						control={Combobox}
						data-testid="etapeBudgetaireDropdown"
						fluid
						label="Etape budgétaire"
						name="etapeBudgetaire"
						onChange={handleChangeEtapeBudgetaire}
						options={ETAPE_BUDGETAIRE_OPTIONS}
						placeholder="Sélectionner une étape budgétaire"
						selection
						value={etapeBudgetaire}
						width={6}
					/>

					<div className="reinit-button">
						<Button onClick={handleReset}>
							{' '}
							Réinitialiser la configuration par défaut{' '}
						</Button>
					</div>
				</Form.Group>
			</Form>

			<DoubleListSelector
				leftValues={leftValues}
				rightValues={rightValues}
				leftTitle="Montants de référence non-affectés"
				rightTitle="Montants de référence affectés"
				iconLabel="montant(s) à affecter"
				onDeleteLine={handleDeleteMontantReference}
				onAddItems={handleAddItems}
				onDragDrop={handleDragDrop}
				loading={loadingMontantsReferences}
			/>
		</>
	);
};

export default MontantReferenceList;
